'use client';

import { useTranslations } from 'next-intl';
import { resolveErrorMessage } from '@/shared/helpers/resolve-error-message';
import { useResetPassword } from '@/features/auth/internal/hooks/use-reset-password';
import { SuccessMessageWithButtonLink } from '@/features/auth/internal/components/success-message-with-button-link';
import { pagesRoutes } from '@/shared/constants/routes';
import { useRouter } from '@/i18n/routing';
import { Form } from '@/shared/components/form/form';
import { InputField } from '@/shared/components/form/input-field';
import { FormSubmit } from '@/shared/components/form/form-submit';
import { FormSubmitError } from '@/shared/components/form/form-submit-error';
import { Button } from '@/shared/components/primitives/button';

type ResetPasswordFormProps = {
  titleId: string;
};

export const ResetPasswordForm = ({ titleId }: ResetPasswordFormProps) => {
  const t = useTranslations('Auth.resetPassword');
  const router = useRouter();
  const { register, handleSubmit, errors, isSubmitting, onSubmit, isSuccess, successEmail } =
    useResetPassword();
  const rootError = errors.root?.message;
  const handleSignInClick = () => {
    router.push(pagesRoutes.login.href);
  };
  const rootErrorContent =
    rootError === 'errors.socialOnly'
      ? t.rich('errors.socialOnly', {
          signIn: (chunks) => (
            <Button
              type='button'
              variant='noStyle'
              size='noStyle'
              className='text-secondary hover:!text-secondary-hover underline'
              onClick={handleSignInClick}
            >
              {chunks}
            </Button>
          ),
        })
      : rootError
        ? rootError.startsWith('errors.')
          ? t(rootError)
          : rootError
        : null;

  return (
    <div>
      {isSuccess ? (
        <SuccessMessageWithButtonLink
          successTitle={t('successTitle')}
          successText={t('successMessage', { email: successEmail })}
          linkText={t('backToLogin')}
          linkHref={pagesRoutes.login.href}
        />
      ) : (
        <Form onSubmit={handleSubmit(onSubmit)} className='mt-8 w-full' aria-labelledby={titleId}>
          <p className='text-center'>{t('description')}</p>
          <div className='mt-4 flex w-full flex-col lg:flex-row lg:items-start lg:gap-4'>
            <InputField
              isRequired
              inputProps={register('email')}
              errorMessage={resolveErrorMessage(errors.email, t)}
              type='email'
              placeholder={t('email')}
              autoComplete='email'
              fieldClassName='w-full flex-1'
              label={t('email')}
            />
            <FormSubmit
              aria-label={t('resetPassword')}
              className='mt-4 w-full lg:mt-8 lg:w-auto lg:shrink-0'
              disabled={isSubmitting}
            >
              {isSubmitting ? t('resettingPassword') : t('resetPassword')}
            </FormSubmit>
          </div>

          {rootErrorContent && (
            <div role='alert'>
              <FormSubmitError className='mt-4 w-full text-center' message={rootErrorContent} />
            </div>
          )}
        </Form>
      )}
    </div>
  );
};
